import { useEffect, useState } from 'react';
import { CircularProgress, Button, Grid, TextField, Alert, Autocomplete, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import { REQUIRED_FIELD, INTERNAL_SERVER_ERROR } from '../../utils/exception';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';

export default function NewBankAccount({ user, getBanks }) {
    const [disabled, setDisabled] = useState(false);

    const [formError, setFormError] = useState(null);
    const [formErrorSeverity, setFormErrorSeverity] = useState('error');

    const [chapaBanks, setChapaBanks] = useState([]);
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [accountClicked, setAccountNumberClicked] = useState(false);
    const [formErrorOnAccountNumber, setFormErrorOnAccountNumber] = useState(false);
    const [helperTextOnAccountNumber, setHelperTextOnAccountNumber] = useState(null);

    const [amountClicked, setBankCodeClicked] = useState(false);
    const [formErrorOnBankCode, setFormErrorOnBankCode] = useState(false);
    const [helperTextOnBankCode, setHelperTextOnBankCode] = useState(null);

    const [accountNameClicked, setAccountNameClicked] = useState(false);
    const [formErrorOnAccountName, setFormErrorOnAccountName] = useState(false);
    const [helperTextOnAccountName, setHelperTextOnAccountName] = useState(null);
    
    // console.log('response: ', chapaBanks)
    useEffect(() => {
        const getChapaBanks = async () => {
            try {
                const response = await axiosInstance.get(`${SERVER_API_URL}/payment/getChapaBanks`);
                if (response.status === 200) {
                    const banks = [];

                    response.data.data.forEach(bank => {
                        banks.push({
                            id: bank.id,
                            name: bank.name,
                            acct_length: bank.acct_length,
                        })
                    });
                    setChapaBanks(banks);
                }
            } catch (e) {
                // console.log(e.response.data.message);
            }
        }
        getChapaBanks();
    }, [])

    const handleSubmitNewPayment = async (event) => {
        event.preventDefault();
        setDisabled(true);

        if (!formErrorOnAccountNumber && !formErrorOnBankCode && !formErrorOnAccountName) {
            const { accountName, bankCode, accountNumber } = event.target.elements;
            try {
                const response = await axiosInstance.post(`${SERVER_API_URL}/payment/newBankAccount/${user && user.id}`, {
                    account_name: accountName.value,
                    bank_code: selectedBankId,
                    bank_name: bankCode.value, // bank name
                    account_number: accountNumber.value,
                });
                if (response.status === 201) {
                    setFormError('አካውንቶ በተሳካ ሁኔታ ተመዝግቧል።');
                    setFormErrorSeverity('success');
                    getBanks();
                }
            } catch (e) {
                if (e.response.status === 406) {
                    setFormError(e.response.data.message);
                    setFormErrorSeverity('error');
                } else if (e.response.status === 500) {
                    setFormError(INTERNAL_SERVER_ERROR);
                    setFormErrorSeverity('error');
                }
            }
        } else {
            setFormError('እባክዎ ባዶ ቦታውን ይሙሉ!');
            setFormErrorSeverity('error');
        }
        setDisabled(false);
    };

    const handleFormValueBlur = (e) => {
        e.preventDefault();
        setFormError(null);
        setFormErrorSeverity('error');

        const { name, value } = e.target;
        switch (name) {
            case 'accountNumber':
                if (value.length === 0) {
                    setAccountNumberClicked(false);
                    setFormErrorOnAccountNumber(accountClicked && value.length === 0);
                    setHelperTextOnAccountNumber(REQUIRED_FIELD);
                } else {
                    setHelperTextOnAccountNumber(null);
                    setFormErrorOnAccountNumber(false);
                }
                break;
            case 'bankCode':
                if (value.length === 0) {
                    setBankCodeClicked(false);
                    setFormErrorOnBankCode(amountClicked && value.length === 0);
                    setHelperTextOnBankCode(REQUIRED_FIELD);
                } else if (!chapaBanks.some((bank) => bank.name === value)) {
                    setFormErrorOnBankCode(true);
                    setHelperTextOnBankCode("የባንክ ስም ከተዘረዘሩት ውስጥ መሆን አለበት።");
                } else {
                    setHelperTextOnBankCode(null);
                    setFormErrorOnBankCode(false);
                }
                break;
            case 'accountName':
                if (value.length === 0) {
                    setAccountNameClicked(false);
                    setFormErrorOnAccountName(accountNameClicked && value.length === 0);
                    setHelperTextOnAccountName(REQUIRED_FIELD);
                } else {
                    setHelperTextOnAccountName(null);
                    setFormErrorOnAccountName(false);
                }
                break;
            default:
                break;
        }
    };

    const handleFormValueChange = (e) => {
        e.preventDefault();
        setFormError(null);
        setFormErrorSeverity('error');
        const { name } = e.target;
        switch (name) {
            case 'accountNumber':
                setFormErrorOnAccountNumber(false);
                setHelperTextOnAccountNumber(null);
                break;
            case 'bankCode':
                setFormErrorOnBankCode(false);
                setHelperTextOnBankCode(null);
                break;
            case 'accountName':
                setFormErrorOnAccountName(false);
                setHelperTextOnAccountName(null);
                break;
            default:
                break;
        }
    };

    const handleClearForm = () => {
        setFormError(null);
        setFormErrorSeverity('error');

        setBankCodeClicked(false);
        setFormErrorOnBankCode(false);
        setHelperTextOnBankCode(null);

        setAccountNameClicked(false);
        setFormErrorOnAccountName(false);
        setHelperTextOnAccountName(null);

        setAccountNumberClicked(false);
        setFormErrorOnAccountNumber(false);
        setHelperTextOnAccountNumber(null);

    };

    return (
        <StyledCardNoHeight
            sx={{ padding: { xs: 1, sm: 5, md: 10 } }}
            component="form"
            autoComplete="off"
            onSubmit={handleSubmitNewPayment}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} sm={8} md={6}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                                አዲስ የተቀባይ ባንክ አካውንት
                            </Typography>
                            <Typography variant="h6" sx={{ ml: 2, color: 'darkgray' }}>
                                መለያ ስም እና መለያ ቁጥር ለመረጡት ባንክ የሚሰራ መሆን አለበት።
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {formError && (
                                <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
                                    {formError}
                                </Alert>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                fullWidth
                                options={chapaBanks || []}
                                getOptionLabel={(option) => option.name}
                                onChange={(event, newValue) => setSelectedBankId(newValue?.id)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        error={formErrorOnBankCode}
                                        fullWidth
                                        name="bankCode"
                                        label="የባንክ ስም"
                                        // variant="standard"
                                        onChange={handleFormValueChange}
                                        helperText={formErrorOnBankCode ? helperTextOnBankCode : null}
                                        onFocus={() => setBankCodeClicked(true)}
                                        onBlur={handleFormValueBlur}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                error={formErrorOnAccountName}
                                fullWidth
                                name="accountName"
                                label="የአካውንት ባለቤት ስም"
                                // variant="standard"
                                onChange={handleFormValueChange}
                                helperText={formErrorOnAccountName ? helperTextOnAccountName : null}
                                onFocus={() => setAccountNameClicked(true)}
                                onBlur={handleFormValueBlur}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                error={formErrorOnAccountNumber}
                                fullWidth
                                name="accountNumber"
                                label="የተቀባይ አካውንት(ስልክ ቁጥር)"
                                // variant="standard"
                                onChange={handleFormValueChange}
                                helperText={formErrorOnAccountNumber ? helperTextOnAccountNumber : null}
                                onFocus={() => setAccountNumberClicked(true)}
                                onBlur={handleFormValueBlur}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', m: 2 }}>
                            <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
                                <RefreshIcon />
                                ቅጽ ዳግም አስጀምር
                            </Button>
                            <Button
                                variant="contained"
                                disabled={disabled}
                                type="submit"
                                sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
                            >
                                {disabled && <CircularProgress style={{ width: '20px', height: '20px' }} />}
                                ቅጽ አስገባ
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </StyledCardNoHeight>
    );
}

NewBankAccount.propTypes = {
    user: PropTypes.object.isRequired,
    getBanks: PropTypes.func.isRequired,
};
